import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getToken } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const FALLBACK_POLICY = {
  policy_version: '2026-07-13',
  title: 'Patient Consent for Remote Patient Monitoring (VITRA)',
  legal_framework: 'Data Protection and Regulations',
  sections: [
    {
      id: 'purpose',
      heading: 'Purpose of the App',
      body:
        'VITRA is a Remote Patient Monitoring (RPM) platform that helps you and your authorized healthcare professionals track health readings, medications, appointments, and care communications. VITRA is not a medical device and does not diagnose, treat, cure, or prevent any disease.',
    },
    {
      id: 'data_collected',
      heading: 'What Data Is Collected',
      body:
        'Identity and contact details; account credentials; MRN; health and clinical data; device/wearable data; appointments and telehealth metadata; messages; emergency contacts; and security/compliance technical logs.',
    },
    {
      id: 'how_used',
      heading: 'How the Data Will Be Used',
      body:
        'To provide RPM services, support your care team, send opted-in reminders, and meet legal and clinical record-keeping obligations. We do not sell your personal data.',
    },
    {
      id: 'who_access',
      heading: 'Who Can Access the Data',
      body:
        'You; your assigned clinicians and authorized care team; administrators under least privilege; and vetted processors acting on our instructions.',
    },
    {
      id: 'data_sharing',
      heading: 'Data Sharing',
      body:
        'Shared with authorized healthcare professionals for care, limited processors for platform operation, and where required by Saudi law or to protect vital interests.',
    },
    {
      id: 'storage_security',
      heading: 'Data Storage and Security',
      body:
        'Stored with access controls, encryption in transit, audit logging, and retention aligned to clinical and legal requirements.',
    },
    {
      id: 'user_rights',
      heading: 'User Rights',
      body:
        'Users may request access, correction, deletion (where allowed), restriction, and withdrawal of consent. Complaints may be lodged with SDAIA (sdaia.gov.sa).',
    },
    {
      id: 'risks',
      heading: 'Risks and Limitations',
      body:
        'Digital health services have residual risks (connectivity, device accuracy, security). VITRA does not replace emergency care.',
    },
    {
      id: 'voluntary',
      heading: 'Voluntary Participation',
      body:
        'Participation is voluntary. Withdrawing required consent suspends RPM processing through the app, subject to legal or clinical obligations.',
    },
    {
      id: 'policy_refs',
      heading: 'Privacy Policy and Terms of Use',
      body: 'Please also read the VITRA Privacy Policy and Terms of Use.',
      links: [
        { label: 'Privacy Policy', path: '/privacy-policy' },
        { label: 'Terms of Use', path: '/terms' },
      ],
    },
    {
      id: 'contact',
      heading: 'Contact Information',
      body:
        'Data Controller: VITRA. Privacy: androidadmin@aces-co.com / appleadmin@aces-co.com. Authority: SDAIA.',
    },
  ],
  consent_statement:
    'I have read and understood the information provided. I voluntarily consent to the collection, use, storage, and processing of my personal and health information for the purposes described above. I understand that I may withdraw my consent at any time, subject to legal or clinical obligations.',
  consent_options: [
    {
      consent_type: 'personal_health_data',
      title: 'Consent to Collect and Use Personal and Health Information',
      description: 'Collect, store, and process identity, contact, and health data for RPM.',
      required: true,
    },
    {
      consent_type: 'share_with_healthcare_professionals',
      title: 'Consent to Share Information with Authorized Healthcare Professionals',
      description: 'Allow your assigned care team to access monitoring data.',
      required: true,
    },
    {
      consent_type: 'appointments_telehealth',
      title: 'Consent for Appointments and Telehealth Services',
      description: 'Scheduling and video consultations.',
      required: false,
    },
    {
      consent_type: 'medication_management',
      title: 'Consent for Medication Management',
      description: 'Medication lists and intake tracking.',
      required: false,
    },
    {
      consent_type: 'notifications_reminders',
      title: 'Consent to Receive Notifications and Reminders',
      description: 'Push/SMS/email reminders.',
      required: false,
    },
    {
      consent_type: 'secure_messaging_ai',
      title: 'Consent for Secure Messaging and AI-Assisted Communication',
      description: 'In-app messaging and optional AI-assisted features.',
      required: false,
    },
    {
      consent_type: 'wearable_device_integration',
      title: 'Consent for Wearable and Medical Device Integration',
      description: 'Link devices and import readings.',
      required: false,
    },
    {
      consent_type: 'emergency_contact',
      title: 'Consent for Emergency Contact Information',
      description: 'Store and use emergency contact details you provide.',
      required: false,
    },
    {
      consent_type: 'privacy_policy',
      title: 'Acknowledgement of Privacy Policy',
      description: 'I have read the Privacy Policy.',
      required: true,
    },
    {
      consent_type: 'terms_of_service',
      title: 'Acknowledgement of Terms of Use',
      description: 'I have read and accept the Terms of Use.',
      required: true,
    },
  ],
  required_consent_types: [
    'personal_health_data',
    'share_with_healthcare_professionals',
    'privacy_policy',
    'terms_of_service',
  ],
};

const ConsentFormPage = () => {
  const [policy, setPolicy] = useState(FALLBACK_POLICY);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [statementAccepted, setStatementAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [gateStatus, setGateStatus] = useState(null);

  const token = getToken();

  const loadPolicy = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/consent-records/policy`, {
        params: { language: 'en' },
      });
      setPolicy(data);
      const initial = {};
      (data.consent_options || []).forEach((opt) => {
        initial[opt.consent_type] = false;
      });
      setSelected(initial);
    } catch {
      setPolicy(FALLBACK_POLICY);
      const initial = {};
      FALLBACK_POLICY.consent_options.forEach((opt) => {
        initial[opt.consent_type] = false;
      });
      setSelected(initial);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGateStatus = useCallback(async () => {
    if (!token) {
      setGateStatus(null);
      return;
    }
    try {
      const { data } = await axios.get(`${API_BASE_URL}/consent-records/me/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGateStatus(data);
      if (data.consents) {
        setSelected((prev) => {
          const next = { ...prev };
          data.consents.forEach((c) => {
            if (c.status === 'granted' && next[c.consent_type] !== undefined) {
              next[c.consent_type] = true;
            }
          });
          return next;
        });
      }
    } catch {
      setGateStatus(null);
    }
  }, [token]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  useEffect(() => {
    loadGateStatus();
  }, [loadGateStatus]);

  const requiredTypes = useMemo(
    () => policy.required_consent_types || FALLBACK_POLICY.required_consent_types,
    [policy]
  );

  const allRequiredChecked = requiredTypes.every((t) => selected[t]);
  const canSubmit = statementAccepted && allRequiredChecked && !!token && !submitting;

  const toggle = (consentType, required) => {
    setSelected((prev) => ({ ...prev, [consentType]: !prev[consentType] }));
    setMessage(null);
    setError(null);
    if (required) {
      // no-op; validation on submit
    }
  };

  const handleAccept = async () => {
    setError(null);
    setMessage(null);
    if (!token) {
      setError('Please sign in with a patient account (mobile login) to record consent.');
      return;
    }
    if (!statementAccepted) {
      setError('You must tick the consent statement checkbox.');
      return;
    }
    if (!allRequiredChecked) {
      setError('Please accept all required consents before continuing.');
      return;
    }

    const consent_types = Object.keys(selected).filter((k) => selected[k]);
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/consent-records/accept`,
        {
          consent_types,
          policy_version: policy.policy_version,
          language: 'en',
          source: 'web_app',
          statement_accepted: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(
        data.required_consents_satisfied
          ? 'Consent recorded. Your account may use RPM services.'
          : `Consent partially recorded. Still missing: ${(data.missing_required_consent_types || []).join(', ')}`
      );
      await loadGateStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to record consent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere">
      <Header />
      <main className="flex-grow w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <p className="text-sm font-semibold tracking-wide text-teal-700 uppercase mb-2">
            VITRA Informed Consent
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
            {policy.title || 'Consent for Remote Patient Monitoring'}
          </h1>
          <p className="text-gray-600">
            Policy version <span className="font-medium text-gray-800">{policy.policy_version}</span>
            {policy.legal_framework ? ` · ${policy.legal_framework}` : null}
          </p>
        </div>

        {gateStatus && (
          <div
            className={`mb-8 rounded-lg border-l-4 p-4 ${
              gateStatus.app_access_allowed
                ? 'border-teal-500 bg-teal-50 text-teal-900'
                : 'border-amber-500 bg-amber-50 text-amber-900'
            }`}
          >
            {gateStatus.app_access_allowed
              ? 'Required consents are on file. RPM access is allowed.'
              : `Required consents missing or withdrawn: ${(gateStatus.missing_required_consent_types || []).join(', ')}. Accept below to restore access.`}
          </div>
        )}

        {loading ? (
          <p className="text-gray-600">Loading consent information…</p>
        ) : (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-8">
            {(policy.sections || []).map((section) => (
              <section key={section.id}>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{section.heading}</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{section.body}</p>
                {section.links?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {section.links.map((link) => (
                      <li key={link.path}>
                        <Link to={link.path} className="text-teal-700 hover:underline font-medium">
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <section className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Consents</h2>
              <p className="text-gray-600 mb-6 text-sm">
                Required items must be accepted for the app to process your health data. Optional
                items enable specific features only.
              </p>
              <div className="space-y-4">
                {(policy.consent_options || []).map((opt) => (
                  <label
                    key={opt.consent_type}
                    className="flex gap-3 items-start p-4 rounded-lg border border-gray-200 hover:border-teal-300 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-teal-600 border-gray-300 rounded"
                      checked={!!selected[opt.consent_type]}
                      onChange={() => toggle(opt.consent_type, opt.required)}
                    />
                    <span>
                      <span className="block font-semibold text-gray-900">
                        {opt.title}
                        {opt.required ? (
                          <span className="ml-2 text-xs font-bold uppercase text-amber-700">Required</span>
                        ) : (
                          <span className="ml-2 text-xs font-bold uppercase text-gray-500">Optional</span>
                        )}
                      </span>
                      <span className="block text-sm text-gray-600 mt-1">{opt.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </section>

            <section className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Consent Statement</h2>
              <label className="flex gap-3 items-start p-4 rounded-lg bg-gray-50 border border-gray-200 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-teal-600 border-gray-300 rounded"
                  checked={statementAccepted}
                  onChange={() => setStatementAccepted((v) => !v)}
                />
                <span className="text-gray-800 leading-relaxed font-medium">
                  {policy.consent_statement}
                </span>
              </label>
            </section>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">
                {error}
              </div>
            )}
            {message && (
              <div className="rounded-lg bg-teal-50 border border-teal-200 text-teal-900 px-4 py-3 text-sm">
                {message}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pt-2">
              <p className="text-sm text-gray-500">
                {token
                  ? 'Consent will be stored in consent_records for record-keeping.'
                  : 'View-only until a patient signs in. Mobile apps should call POST /consent-records/accept after login.'}
              </p>
              <button
                type="button"
                disabled={!canSubmit}
                onClick={handleAccept}
                className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                  canSubmit
                    ? 'bg-teal-600 hover:bg-teal-700'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {submitting ? 'Recording…' : 'I Accept & Record Consent'}
              </button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ConsentFormPage;
